import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { confirmPaymentAndSignIn } from "@/lib/stripe.functions";
// Post-checkout, drop users on /welcome. The interstitial self-handles the
// "already welcomed" case by short-circuiting to lesson 1.1, so returning
// users via magic link don't see it again.
const POST_SIGNIN_PATH = "/welcome";
import { authClient } from "@/lib/auth";
import { SiteHeader } from "@/components/SiteHeader";

const MONO = '"JetBrains Mono", monospace';
const SERIF = '"Cormorant Garamond", serif';

export const Route = createFileRoute("/success")({
  validateSearch: (s: Record<string, unknown>) => ({
    session_id: typeof s.session_id === "string" ? s.session_id : undefined,
  }),
  head: () => ({
    meta: [
      { title: "You're in — WHO KNEW" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SuccessPage,
});

function SuccessPage() {
  const { session_id } = Route.useSearch();
  const navigate = useNavigate();
  const confirm = useServerFn(confirmPaymentAndSignIn);
  const [status, setStatus] = useState<"working" | "error">("working");
  const [message, setMessage] = useState<string>("Confirming your payment…");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!session_id) {
        setStatus("error");
        setMessage("Missing payment session.");
        return;
      }
      try {
        const res = await confirm({ data: { session_id } });
        if (cancelled) return;
        if (!res.ok) {
          setStatus("error");
          setMessage(
            res.error === "not_paid"
              ? "We couldn't confirm your payment. If you were charged, email hello@whoknew.guide."
              : "Something went wrong signing you in. Email hello@whoknew.guide and we'll sort it.",
          );
          return;
        }
        setMessage("Signing you in…");
        const { error } = await authClient.auth.verifyOtp({
          type: "magiclink",
          token_hash: res.token_hash,
        });
        if (cancelled) return;
        if (error) {
          setStatus("error");
          setMessage(error.message);
          return;
        }
        try {
          window.localStorage.setItem("last_signed_in_email", res.email);
        } catch {
          // ignore
        }
        navigate({ to: POST_SIGNIN_PATH, replace: true });
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setMessage(err instanceof Error ? err.message : "Unknown error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session_id, confirm, navigate]);

  return (
    <div className="min-h-screen bg-paper text-ink">
      <SiteHeader />
      <main className="flex min-h-screen items-center justify-center px-6">
        <div className="max-w-[560px] text-center" style={{ paddingTop: 80, paddingBottom: 80 }}>
          <p
            className="font-bold uppercase tracking-[0.05em] text-burgundy"
            style={{ fontFamily: MONO, fontSize: 11 }}
          >
            {status === "working" ? "ONE MOMENT" : "SOMETHING WENT WRONG"}
          </p>
          <h1
            className="text-[28px] md:text-[40px] font-bold leading-[1.15] text-ink"
            style={{ marginTop: 24 }}
          >
            {status === "working" ? "You're in." : "We hit a snag."}
          </h1>
          <p
            className="italic"
            style={{
              fontFamily: SERIF,
              color: "#5A534A",
              fontSize: 18,
              lineHeight: 1.5,
              marginTop: 24,
            }}
          >
            {message}
          </p>
        </div>
      </main>
    </div>
  );
}
